import { ProfileDto } from '@diritto/common/account/dtos/profile.dto';
import { ContentDto } from '@diritto/common/content/dtos/content.dto';
import { EventType } from '@diritto/common/event/constants';
import { EventExtraInfo } from '@diritto/common/event/types/event-extra-info-types';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC } from 'constants/layout';
import { getTextLineStyle } from 'styles/common';
import { Link } from 'ui-components/common/Link';
import { OldContentCover } from 'ui-components/contents/ContentCover';
import { EventBodyContainer } from 'ui-components/layout/containers/EventBodyContainer';
import { ImageRatio } from '@diritto/common/base/constants';

interface NewContentPromoteEventHeaderProps {
  info: EventExtraInfo[EventType.NewContentPromoteEvent];
  contents: Record<number, ContentDto>;
  profiles: Record<number, ProfileDto>;
}

const CoverSize = {
  PC: 150,
};

export const NewContentPromoteEventHeader = ({ info, contents, profiles }: NewContentPromoteEventHeaderProps) => (
  <EventBodyContainer>
    <StylesList isOnlyOne={info.payload.contentIds.length === 1}>
      {info.payload.contentIds.map(contentId => {
        const content = contents[contentId];
        if (!content) {
          return null;
        }

        const profile = content.profileId && profiles[content.profileId];

        return (
          <StyledContainer key={contentId}>
            <StyledWrapper>
              <Link href={`/contents/${content.id}`}>
                <StyledContentCover content={content} />
                <StyledTitle>{content.title}</StyledTitle>
              </Link>
              {profile && <StyledAuthor>{profile.nickname}</StyledAuthor>}
            </StyledWrapper>
          </StyledContainer>
        );
      })}
    </StylesList>
  </EventBodyContainer>
);

const StylesList = styled.div(
  ({ isOnlyOne }: { isOnlyOne: boolean }) => css`
    display: grid;
    grid-gap: 16px;
    grid-template-columns: ${isOnlyOne ? 'initial' : `repeat(2, ${CoverSize.PC}px)`};
    text-align: initial;
  `,
);

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media (${isPC}) {
    justify-content: initial;
  }
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: min-content;
`;

const StyledContentCover = styled(OldContentCover)`
  width: ${CoverSize.PC}px;
  height: ${CoverSize.PC * ImageRatio}px;
`;

const StyledTitle = styled.p`
  margin-top: 10px;
  font-weight: ${FontWeight.Bold};
  ${Typography.XXSmall};
  ${getTextLineStyle(3)};

  @media (${isPC}) {
    ${Typography.XSmall};
  }
`;

const StyledAuthor = styled.p`
  margin-top: 4px;
  flex-basis: fit-content;
  color: ${Color.Gray700};
  ${Typography.XXXSmall};
  ${getTextLineStyle(1)};

  @media (${isPC}) {
    ${Typography.XXSmall};
  }
`;
